import React from "react";
import { ErrorMessage, Field, Formik } from "formik";
import { useEffect, useState, useRef } from "react";
import { Alert, Button, Col, Form, Row } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { LoadingSpinner } from "../../components/LoadingSpinner";
import { ILead } from "@/customTypings/Lead";
import { IAddress } from "../../customTypings/Address";
import { IAddressOption } from "../../customTypings/AddressOption";
import { IContact } from "../../customTypings/Contact";
import { useAtom } from "@dbeining/react-atom";
import Select from "react-select";
import FormikSelect, { OptionType } from "../../components/FormikSelect";
import SelectErrorMessage from "../../components/SelectErrorMesage";
import { toCamelCase } from "../../helpers/to-camel-case";

import {
  addressService,
  contactService,
  leadService,
  postcodeService,
  userService,
} from "../../services";
import { ReactSelectBootstrapStyle } from "../../styles/ReactSelectBootstrapStyle";
import { userAtom } from "../../atoms/userAtom";

function LeadAddForm() {
  // State declarations
  const [alertVariant, setAlertVariant] = useState("danger");
  const [allUsers, setAllUsers] = useState<any[]>([]);
  const [isLooking, setIsLooking] = useState(false);
  const [lookupOptions, setLookupOptions] = useState<{ value: string; label: string; address: any }[]>([]);
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const [selectedLookup, setSelectedLookup] = useState<{ value: string; label: string; address: any } | null>(null);
  const [disableAutoOpen, setDisableAutoOpen] = useState(false);
  const lookupSelectRef = useRef<any>(null);

  useEffect(() => {
    if (lookupOptions.length > 0 && lookupSelectRef.current) {
      lookupSelectRef.current.focus();
    }
  }, [lookupOptions]);

  const mapPostcodeResponseToOptions = (response: any): { value: string; label: string; address: any }[] => {
    return response?.delivery_points.map((deliveryPoint: any) => ({
      value: deliveryPoint.udprn,
      label: `${deliveryPoint.line_1}, ${deliveryPoint.line_2}, ${response.town}, ${response.postcode}`,
      address: {
        line1: deliveryPoint.line_1,
        line2: deliveryPoint.line_2 || "",
        town: response.town,
        postcode: response.postcode,
        country: response.country || "UK",
      },
    }));
  };

  const leadSourceOptions: OptionType[] = [
    { value: "Website", label: "Website" },
    { value: "Walk-in", label: "Walk-in" },
    { value: "Telephone", label: "Telephone" },
    { value: "Email", label: "Email" },
  ];

  const contactDepartmentOptions: OptionType[] = [
    { value: "Service", label: "Service" },
    { value: "Online", label: "Online" },
    { value: "Sales", label: "Sales" },
  ];

  useEffect(() => {
    Promise.all([userService.getAllListItems()]).then((response) => {
      setAllUsers(response[0]);
    });
  }, []);

  const navigate = useNavigate();
  const currentUser = useAtom(userAtom);

  // Define the form values (this is our form model, not the full Lead)
  const formInitialValues = {
    leadNumber: "",
    title: "Mr.",
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    line1: "",
    line2: "",
    town: "",
    country: "",
    postCode: "",
    isBusiness: "false",
    leadSource: "",
    assignedToPersonnel: "",
    contactDepartment: "",
  };

  const formatFieldLabel = (field: string) =>
    field.replace(/([a-z])([A-Z])/g, "$1 $2").replace(/^./, (str) => str.toUpperCase());

  const fieldDisplayNames: { [key: string]: string } = {
    line1: "Address Line 1",
    line2: "Address Line 2",
    postCode: "Postcode",
  };

  const generateValidationSchema = () => {
    const schemaObject: { [key: string]: Yup.StringSchema<string | undefined> } = {};
    Object.keys(formInitialValues).forEach((field) => {
      let fieldSchema;
      if (field === "email") {
        fieldSchema = Yup.string()
          .trim()
          .required(`${formatFieldLabel(field)} is required`)
          .email(`${formatFieldLabel(field)} must be a valid email`);
      } else if (field === "leadNumber") {
        fieldSchema = Yup.string().matches(/^\d+$/, `${formatFieldLabel(field)} must contain only numbers`);
      } else if (field === "phone") {
        fieldSchema = Yup.string()
          .required("Phone Number is required")
          .matches(/^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/, "Invalid phone number");
      } else if (field === "line1") {
        fieldSchema = Yup.string().required(`${fieldDisplayNames.line1 || formatFieldLabel(field)} is required`);
      } else if (field === "line2") {
        fieldSchema = Yup.string();
      } else if (field === "postCode") {
        fieldSchema = Yup.string().required(`${fieldDisplayNames.postCode} is required`);
      } else if (field === "leadSource") {
        fieldSchema = Yup.string()
          .trim()
          .transform((value, originalValue) => (originalValue.trim() === "" ? undefined : value))
          .required("Lead Source is required");
      } else if (field === "assignedToPersonnel") {
        fieldSchema = Yup.string()
          .trim()
          .transform((value, originalValue) => (originalValue.trim() === "" ? undefined : value))
          .required("Assigned To Personnel is required");
      } else if (field === "contactDepartment") {
        // Optional field.
        fieldSchema = Yup.string();
      } else {
        fieldSchema = Yup.string().required(`${formatFieldLabel(field)} is required`);
      }
      schemaObject[field] = fieldSchema;
    });
    return Yup.object().shape(schemaObject);
  };

  const save = async (
    values: any,
    setStatus: any,
    setSubmitting: any,
    setFieldError: any,
    resetForm: any
  ) => {
    const fullName = `${values.title} ${values.firstName} ${values.lastName}`.trim();

    // Build updated Contact object.
    const updatedContact: IContact = {
      id: undefined,
      name: fullName,
      emailAddress: values.email,
      mobilePhone: values.phone,
      workPhone: "",
      email2: "",
    };

    // Build updated Address object.
    const updatedAddress: IAddress = {
      id: undefined,
      line1: values.line1,
      line2: values.line2 || null,
      line3: values.line3 || null,
      town: values.town,
      county: values.country, // Form "country" maps to Address.county.
      postcode: values.postCode,
      what3words: "null",
      latitude: "",
      longitude: "",
    };

    const contact = await contactService.add(updatedContact);
    const address = await addressService.add(updatedAddress);

    // Build updated Lead object using ILead.
    const updatedLead: ILead = {
      id: undefined,
      identityId: currentUser?.identity.id,
      leadNumber: `${values.leadNumber}`,
      name: fullName,
      isBusiness: values.isBusiness === "true",
      dateOfInquiry: new Date(),
      notes: "",
      contactId: contact.id,
      addressId: address.id,
      assignedToPersonnel: values.assignedToPersonnel || currentUser?.identity.id,
      leadSource: values.leadSource || leadSourceOptions[0].value,
      contactDepartment: values.contactDepartment,
    };

    leadService.add(updatedLead).then(
      (response) => {
        setSubmitting(false);
        if (response.status === "Success") {
          resetForm({ values: "" });
          setAlertVariant("success");
          setStatus("Lead has been created successfully");
          navigate("/leads");
        } else {
          setAlertVariant("danger");
          setStatus(response.message);
        }
      },
      (error) => {
        setAlertVariant("danger");
        setStatus(error.title);
      }
    );
  };

  const lookUpPostCode = (postcode: string, setStatus: (msg?: string) => void) => {
    if (!postcode.trim()) return;
    setIsLooking(true);
    postcodeService.lookup(postcode).then((res: any) => {
      if (res.delivery_points !== null) {
        const options = mapPostcodeResponseToOptions(res);
        setAlertVariant("success");
        setLookupOptions(options);
        setSelectedLookup(null);
        setMenuIsOpen(true);
        setIsLooking(false);
      } else {
        setAlertVariant("danger");
        setLookupOptions([]);
        setMenuIsOpen(false);
        setIsLooking(false);
      }
    });
  };

  return (
    <>
      <h1 className="my-3">Add Lead</h1>
      <Formik
        initialValues={{ ...formInitialValues }}
        validationSchema={generateValidationSchema()}
        onSubmit={(
          values,
          { setStatus, setSubmitting, setFieldError, resetForm, setFieldValue }
        ) => {
          save(values, setStatus, setSubmitting, setFieldError, resetForm);
        }}
      >
        {({
          errors,
          status,
          touched,
          isSubmitting,
          handleSubmit,
          values,
          setStatus,
          handleChange,
          setFieldValue,
          setFieldError,
          setFieldTouched,
        }) => (
          <Form onSubmit={handleSubmit}>
            {/* Title */}
            <Form.Group as={Row} controlId="title" className="mb-3">
              <Form.Label column sm={3}>Title</Form.Label>
              <Col sm={9}>
                <Field
                  as="select"
                  name="title"
                  onChange={handleChange}
                  value={values.title}
                  className={`form-control ${errors.title && touched.title ? "is-invalid" : ""}`}
                >
                  <option value="Mr.">Mr.</option>
                  <option value="Mrs.">Mrs.</option>
                  <option value="Miss.">Miss.</option>
                  <option value="Ms.">Ms.</option>
                  <option value="Mx.">Mx.</option>
                </Field>
                <ErrorMessage name="title" component="div" className="invalid-feedback" />
              </Col>
            </Form.Group>
            {Object.keys(formInitialValues)
              .filter((field) =>
                ![
                  "leadNumber",
                  "postCode",
                  "id",
                  "title",
                  "isBusiness",
                  "line1",
                  "line2",
                  "town",
                  "country",
                  "contactDepartment",
                  "leadSource",
                  "assignedToPersonnel",
                ].includes(field)
              )
              .map((field) => (
                <Form.Group as={Row} key={field} className="mb-3" controlId={field}>
                  <Form.Label column sm={3}>
                    {formatFieldLabel(field)} <span style={{ color: "red" }}>*</span>
                  </Form.Label>
                  <Col sm={9}>
                    <Field
                      name={field}
                      onChange={handleChange}
                      type="text"
                      value={values[field as keyof typeof formInitialValues]}
                      className={`form-control ${
                        errors[field as keyof typeof formInitialValues] &&
                        touched[field as keyof typeof formInitialValues]
                          ? "is-invalid"
                          : ""
                      }`}
                    />
                    <ErrorMessage name={field} component="div" className="invalid-feedback" />
                  </Col>
                </Form.Group>
              ))}
            <Form.Group as={Row} controlId="postCode" className="mb-3">
              <Form.Label column sm={3}>
                {fieldDisplayNames.postCode || formatFieldLabel("postCode")} <span style={{ color: "red" }}>*</span>
              </Form.Label>
              <Col sm={9}>
                <Row>
                  <Col xs={3} lg={3}>
                    <Field
                      name="postCode"
                      onChange={handleChange}
                      onKeyDown={(e: React.KeyboardEvent) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          if (values.postCode && values.postCode.trim()) {
                            lookUpPostCode(values.postCode, setStatus);
                          }
                        }
                      }}
                      type="text"
                      value={values.postCode}
                      className={`form-control ${errors.postCode && touched.postCode ? "is-invalid" : ""}`}
                    />
                    <ErrorMessage name="postCode" component="div" className="invalid-feedback" />
                  </Col>
                  <Col xs={3} lg={2}>
                    <Button
                      variant="primary"
                      onClick={() => {
                        if (values.postCode && values.postCode.trim()) {
                          lookUpPostCode(values.postCode, setStatus);
                        }
                      }}
                      disabled={!values.postCode || !values.postCode.trim() || isLooking}
                    >
                      {isLooking ? (
                        <span style={{ display: "inline-flex", alignItems: "center" }}>
                          <LoadingSpinner text="Looking..." />
                        </span>
                      ) : (
                        "Look Up"
                      )}
                    </Button>
                  </Col>
                  {lookupOptions.length > 0 && (
                    <Col xs={12} lg={7} className="mt-3 mt-lg-0">
                      <div onClick={() => lookupOptions.length > 0 && setMenuIsOpen(true)}>
                        <Select
                          ref={lookupSelectRef}
                          value={selectedLookup}
                          styles={ReactSelectBootstrapStyle}
                          className="stateManagedSelect"
                          options={lookupOptions}
                          isClearable
                          menuIsOpen={menuIsOpen}
                          onMenuClose={() => setMenuIsOpen(false)}
                          onFocus={() => {
                            if (!disableAutoOpen && lookupOptions.length > 0) {
                              setMenuIsOpen(true);
                            }
                          }}
                          onChange={(
                            selectedOption: { value: string; label: string; address: IAddressOption } | null
                          ) => {
                            if (!selectedOption) {
                              setSelectedLookup(null);
                              return;
                            }
                            const { address } = selectedOption;
                            setFieldValue("line1", toCamelCase(address.line1));
                            setFieldValue("line2", toCamelCase(address.line2));
                            setFieldValue("town", toCamelCase(address.town));
                            setFieldValue("postCode", toCamelCase(address.postcode));
                            setFieldValue("country", address.country);
                            setFieldError("line1", "");
                            setFieldError("town", "");
                            setFieldError("postCode", "");
                            setFieldError("country", "");
                            setFieldTouched("line1", false);
                            setFieldTouched("town", false);
                            setFieldTouched("postCode", false);
                            setFieldTouched("country", false);
                            setSelectedLookup(selectedOption);
                            setMenuIsOpen(false);
                            setDisableAutoOpen(true);
                            setTimeout(() => setDisableAutoOpen(false), 200);
                          }}
                        />
                      </div>
                    </Col>
                  )}
                </Row>
              </Col>
            </Form.Group>
            <Form.Group as={Row} controlId="line1" className="mb-3">
              <Form.Label column sm={3}>
                {fieldDisplayNames.line1 || formatFieldLabel("line1")} <span style={{ color: "red" }}>*</span>
              </Form.Label>
              <Col sm={9}>
                <Field
                  name="line1"
                  onChange={handleChange}
                  type="text"
                  value={values.line1}
                  className={`form-control ${errors.line1 && touched.line1 ? "is-invalid" : ""}`}
                />
                <ErrorMessage name="line1" component="div" className="invalid-feedback" />
              </Col>
            </Form.Group>
            <Form.Group as={Row} controlId="line2" className="mb-3">
              <Form.Label column sm={3}>
                {fieldDisplayNames.line2 || formatFieldLabel("line2")}
              </Form.Label>
              <Col sm={9}>
                <Field
                  name="line2"
                  onChange={handleChange}
                  type="text"
                  value={values.line2}
                  className={`form-control ${errors.line2 && touched.line2 ? "is-invalid" : ""}`}
                />
                <ErrorMessage name="line2" component="div" className="invalid-feedback" />
              </Col>
            </Form.Group>
            <Form.Group as={Row} controlId="town" className="mb-3">
              <Form.Label column sm={3}>
                {formatFieldLabel("town")} <span style={{ color: "red" }}>*</span>
              </Form.Label>
              <Col sm={9}>
                <Field
                  name="town"
                  onChange={handleChange}
                  type="text"
                  value={values.town}
                  className={`form-control ${errors.town && touched.town ? "is-invalid" : ""}`}
                />
                <ErrorMessage name="town" component="div" className="invalid-feedback" />
              </Col>
            </Form.Group>
            <Form.Group as={Row} controlId="country" className="mb-3">
              <Form.Label column sm={3}>
                {formatFieldLabel("country")} <span style={{ color: "red" }}>*</span>
              </Form.Label>
              <Col sm={9}>
                <Field
                  name="country"
                  onChange={handleChange}
                  type="text"
                  value={values.country}
                  className={`form-control ${errors.country && touched.country ? "is-invalid" : ""}`}
                />
                <ErrorMessage name="country" component="div" className="invalid-feedback" />
              </Col>
            </Form.Group>
            {/* Lead Source */}
            <Form.Group as={Row} controlId="leadSource" className="mb-3">
              <Form.Label column sm={3}>
                Lead Source <span style={{ color: "red" }}>*</span>
              </Form.Label>
              <Col sm={9}>
                <FormikSelect
                  styles={ReactSelectBootstrapStyle}
                  className="stateManagedSelect"
                  name="leadSource"
                  isClearable
                  options={leadSourceOptions}
                />
                <SelectErrorMessage name="leadSource" />
              </Col>
            </Form.Group>
            {/* Contact Department */}
            <Form.Group as={Row} controlId="contactDepartment" className="mb-3">
              <Form.Label column sm={3}>
                Contact Department <span style={{ color: "red" }}>*</span>
              </Form.Label>
              <Col sm={9}>
                <FormikSelect
                  styles={ReactSelectBootstrapStyle}
                  className="stateManagedSelect"
                  name="contactDepartment"
                  isClearable
                  options={contactDepartmentOptions}
                />
                <SelectErrorMessage name="contactDepartment" />
              </Col>
            </Form.Group>
            {/* Assigned To Personnel */}
            <Form.Group as={Row} controlId="assignedToPersonnel" className="mb-3">
              <Form.Label column sm={3}>
                Assigned To Personnel <span style={{ color: "red" }}>*</span>
              </Form.Label>
              <Col sm={9}>
                <FormikSelect
                  styles={ReactSelectBootstrapStyle}
                  className="stateManagedSelect"
                  name="assignedToPersonnel"
                  isClearable
                  options={allUsers}
                />
                <SelectErrorMessage name="assignedToPersonnel" />
              </Col>
            </Form.Group>
            <Button
              variant="primary"
              disabled={isSubmitting}
              type="submit"
              className="me-2 d-flex align-items-end ms-auto"
            >
              {isSubmitting ? (
                <span style={{ display: "inline-flex", alignItems: "center" }}>
                  <LoadingSpinner text="Saving..." />
                </span>
              ) : (
                "Save"
              )}
            </Button>
            {status && (
              <Alert variant={alertVariant} className="mt-3">
                {status}
              </Alert>
            )}
          </Form>
        )}
      </Formik>
    </>
  );
}

export default LeadAddForm;